import Raven from 'raven-js';
import { useCallback, useMemo, useRef, useState } from 'react';
import debounce from 'react-utils/debounce';
import uniqueId from 'transmute/uniqueId';
import { useHttpClient } from '../../client/HttpClientContext';
import { Metrics } from '../../metrics/Metrics';
import { useDelayedState } from '../../utils/hooks/useDelayedState';
import { trackAutosaveSuccess, trackAutosaveUndo } from '../../utils/usageTrackingUtils';
import { savePropertyValues } from '../api';
import { useAutosavePackets } from './useAutosavePackets';
import { useAutosaveQueue } from './useAutosaveQueue';
import { useAutosaveWindowEvents } from './useAutosaveWindowEvents';
/**
 * Creates a batch for each object type id. Our BE api can handle multiple objectId updates per
 * objectTypeId. Each batch will correspond to one network request.
 */
const getBatches = items => {
  const batches = items.reduce((acc, item) => {
    const {
      request,
      onSuccess,
      onFailure,
      onUndo
    } = item;
    const {
      objectTypeId,
      objectId,
      properties,
      previousProperties
    } = request;
    if (!acc[objectTypeId]) {
      acc[objectTypeId] = {
        objectTypeId,
        updates: {},
        previousUpdates: {},
        successCallbacks: [],
        failureCallbacks: [],
        undoCallbacks: []
      };
    }
    if (!acc[objectTypeId].updates[objectId]) {
      acc[objectTypeId].updates[objectId] = {};
    }
    if (!acc[objectTypeId].previousUpdates[objectId]) {
      acc[objectTypeId].previousUpdates[objectId] = {};
    }
    properties.forEach(({
      name,
      value
    }) => {
      acc[objectTypeId].updates[objectId][name] = value;
    });
    previousProperties.forEach(({
      name,
      value
    }) => {
      acc[objectTypeId].previousUpdates[objectId][name] = value;
    });
    acc[objectTypeId].successCallbacks.push(onSuccess);
    acc[objectTypeId].failureCallbacks.push(onFailure);
    acc[objectTypeId].undoCallbacks.push(onUndo);
    return acc;
  }, {});
  return Object.values(batches);
};
export const useAutosaveCore = () => {
  const httpClient = useHttpClient();
  const [status, setStatusWithDelay] = useDelayedState('idle');
  const [handleUndo, setHandleUndo] = useState(() => () => {});
  const handlersRef = useRef(new Map());
  const [lastSavedPacket, setLastSavedPacket] = useState(null);
  const {
    addItems,
    clear,
    filter: filterQueue,
    enqueue,
    getItems,
    hasSourceId,
    removeItemBySourceId
  } = useAutosaveQueue();
  const {
    getPackets,
    pausePacketsForSource,
    addPacket,
    removePacket
  } = useAutosavePackets();
  useAutosaveWindowEvents(getPackets);
  const save = useCallback(items => {
    const packetId = uniqueId('packet-');
    const batches = getBatches(items);
    setHandleUndo(() => () => {
      for (const batch of batches) {
        const {
          objectTypeId,
          previousUpdates,
          undoCallbacks,
          failureCallbacks
        } = batch;
        const previousPropertyNames = Object.values(previousUpdates).flatMap(update => Object.keys(update));
        trackAutosaveUndo({
          objectTypeId,
          propertyNames: previousPropertyNames
        });
        savePropertyValues(objectTypeId, previousUpdates, httpClient).catch(e => {
          failureCallbacks.forEach(callback => callback(e));
        }).then(result => {
          Metrics.counter('autosave-undo-success').increment();
          if (result) {
            undoCallbacks.forEach(callback => callback(result));
          }
        }).catch(e => {
          Metrics.counter('autosave-undo-failure').increment();
          Raven.captureException(new Error('Autosave undo handler failed'), {
            extra: {
              objectTypeId,
              propertyNames: previousPropertyNames,
              originalError: e
            }
          });
        });
      }
      setStatusWithDelay('reverted');
      setStatusWithDelay('idle', 3000);
    });
    const packet = {
      packetId,
      items
    };
    addPacket(packet);
    setStatusWithDelay('pending');
    Promise.allSettled(batches.map(batch => {
      const {
        objectTypeId,
        updates,
        successCallbacks,
        failureCallbacks
      } = batch;
      const propertyNames = Object.values(updates).flatMap(update => Object.keys(update));
      return savePropertyValues(objectTypeId, updates, httpClient).catch(e => {
        failureCallbacks.forEach(callback => callback(e));
      }).then(result => {
        if (result) {
          trackAutosaveSuccess({
            propertyNames,
            objectTypeId
          });
          Metrics.counter('autosave-success', {
            propertyCount: String(propertyNames.length)
          }).increment();
          successCallbacks.forEach(callback => callback(result));
          return true;
        }
      }).catch(e => {
        Metrics.counter('autosave-onsave-failure').increment();
        Raven.captureException(new Error('Autosave success handler failed'), {
          extra: {
            objectTypeId,
            propertyNames,
            originalError: e
          }
        });
        return true;
      });
    })).then(results => {
      const allSuccess = results.every(result => result.status === 'fulfilled' && result.value);
      if (allSuccess) {
        setStatusWithDelay('success');
        setStatusWithDelay('idle', 10000);
      } else {
        setStatusWithDelay('idle');
      }
    }).catch(() => {}).finally(() => {
      setLastSavedPacket(packet);
      removePacket(packetId);
    });
  }, [addPacket, setStatusWithDelay, httpClient, removePacket]);
  const saveValidRequests = useCallback(() => {
    const handlers = Array.from(handlersRef.current.values());
    const itemsToProcess = getItems().filter(item => handlers.every(({
      getIsHandlerForRequest,
      getIsValidRequest
    }) => !getIsHandlerForRequest(item.request) || getIsValidRequest(item.request)));
    if (itemsToProcess.length > 0) {
      filterQueue(item => !itemsToProcess.includes(item));
      save(itemsToProcess);
    }
  }, [save, getItems, filterQueue, handlersRef]);
  const cancelRequestsForSource = useCallback(sourceId => {
    pausePacketsForSource(sourceId, (packet, index) => {
      addItems(packet.items);
      if (index === 0) {
        setStatusWithDelay('reverted');
        setStatusWithDelay('idle', 3000);
        Metrics.counter('autosave-cancelled-request').increment();
      }
    });
    removeItemBySourceId(sourceId);
  }, [pausePacketsForSource, setStatusWithDelay, addItems, removeItemBySourceId]);
  const clearPendingRequests = useCallback(() => {
    const items = getItems();
    clear();
    for (const item of items) {
      item.onClear();
    }
    setStatusWithDelay('idle', 3000);
    Metrics.counter('autosave-clear-pending-requests').increment();
  }, [clear, getItems, setStatusWithDelay]);
  const enqueueRequest = useCallback(args => {
    enqueue(Object.assign({}, args, {
      timestamp: Date.now()
    }));
  }, [enqueue]);
  const registerHandler = useCallback(config => {
    handlersRef.current.set(config.id, config);
    return () => {
      handlersRef.current.delete(config.id);
    };
  }, []);
  const items = getItems();
  const requests = useMemo(() => items.map(item => item.request), [items]);
  const invalidRequests = useMemo(() => {
    const handlers = Array.from(handlersRef.current.values());
    return items.filter(item => handlers.some(({
      getIsHandlerForRequest,
      getIsValidRequest
    }) => getIsHandlerForRequest(item.request) && !getIsValidRequest(item.request))).map(item => item.request);
  }, [items]);
  return {
    cancelRequestsForSource,
    clearPendingRequests,
    enqueueRequest,
    handleUndo,
    hasRequestForSource: hasSourceId,
    invalidRequests,
    lastSavedPacket,
    packets: getPackets(),
    registerHandler,
    requests,
    saveValidRequests: useMemo(() => debounce(saveValidRequests, 25), [saveValidRequests]),
    status
  };
};